import React, { useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import PropTypes from 'prop-types';

import { GATE_DRAG_OPTIONS, GATE_SET_OPTIONS, GATE_TYPES, gateTitleToHeading, handleEmptyDrop } from './helper';
import MoveableGate from './moveable_gate';
import UnusedAccessLevelsTooltip from './unused_access_levels_tooltip';
import Banner from 'shared/banner';

export default function RoleAccessSettings({ gates, role, vaultId, onGatesChange }) {
  const [invalidDragTitles, setInvalidDragTitles] = useState([]);
  const removeDragRefs = useRef({});
  const dragIdentifier = `gate-${role.label}-${vaultId}`;

  function addDragTitles(dragTitlesToAdd) {
    dragTitlesToAdd.forEach(dragTitle => clearTimeout(removeDragRefs.current[dragTitle]));

    setInvalidDragTitles((dragTitles) => {
      const newDragTitles = dragTitlesToAdd.filter(dragTitle => !dragTitles.includes(dragTitle));

      return [...dragTitles, ...newDragTitles];
    });
  }

  function removeDragTitles(dragTitlesToRemove, timeout) {
    dragTitlesToRemove.forEach(dragTitle => clearTimeout(removeDragRefs.current[dragTitle]));
    if (timeout) {
      dragTitlesToRemove.forEach((dragTitle, index) => {
        const removal = () => setInvalidDragTitles(dragTitles => dragTitles.filter(title => title !== dragTitle));
        removeDragRefs.current[dragTitle] = setTimeout(removal, timeout + (index * 200));
      });
    } else {
      setInvalidDragTitles(dragTitles => dragTitles.filter(title => !dragTitlesToRemove.includes(title)));
    }
  }

  function handleUpdatedGates(newGates) {
    onGatesChange(newGates, vaultId, role.type);
  }

  function handleEmptyGateDrop(index) {
    const updatedGates = handleEmptyDrop(gates, index);
    onGatesChange(updatedGates, vaultId, role.type);
  }

  function invalidDragText(dragTitle) {
    return (
      <span>
        <span className="fw-semibold">{gateTitleToHeading(dragTitle)}</span>
        {' '}has to have one of{' '}
        <span className="fw-semibold">Collect Information</span>
        {' '}or{' '}
        <span className="fw-semibold">Verification</span>
        {' '}before it
      </span>
    );
  }

  const [{ handlerId }, drop] = useDrop({
    accept: dragIdentifier,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!monitor.canDrop()) return;

      handleEmptyGateDrop(item.index);
      item.type = GATE_TYPES.NOT_IN_PROGRESS;
      item.index = 0;
    },
    canDrop() {
      return gates[GATE_TYPES.NOT_IN_PROGRESS].length === 0;
    }
  });

  function allowDropForGate(gateType, index, gateStatus) {
    if (gateStatus === GATE_TYPES.NOT_IN_PROGRESS || !GATE_SET_OPTIONS.includes(gateType)) {
      return { left: true, right: true };
    }

    return { left: index !== 0, right: index !== gates[GATE_TYPES.IN_PROGRESS].length - 1 };
  }

  let gateZIndex = 10;

  const alertContent = (
    <ul className="m-0" style={{ fontSize: '14px', paddingLeft: '20px' }}>
      {invalidDragTitles.map(dragTitle => (
        <li key={dragTitle}>{invalidDragText(dragTitle)}</li>
      ))}
    </ul>
  );

  const gateTypesInProgress = gates[GATE_TYPES.IN_PROGRESS].map(gate => gate.gateType);
  const unusedAccessLevels = GATE_SET_OPTIONS.filter(gateType => !gateTypesInProgress.includes(gateType));

  return (
    <React.Fragment>
      <div>
        <div className="fs-large fw-semibold mb-1">Define {role.label} Access</div>
        <div>
          Drag and drop each step for brokers to gain access to each level of documents. This process
          will allow you to customize a series of steps that a broker will go through to gain access to
          different levels of documents on your website.
        </div>
      </div>
      <div>
        <div className="fw-semibold mb-1">Select actions from this list</div>
        <div
          className="d-flex panel panel-default soft-panel m-0 gate-container"
          data-handler-id={handlerId}
          ref={drop}
        >
          {gates[GATE_TYPES.NOT_IN_PROGRESS].map((dragCard, index) => (
            <MoveableGate
              allowedDropLocations={allowDropForGate(dragCard.gateType, index, GATE_TYPES.NOT_IN_PROGRESS)}
              dragIdentifier={dragIdentifier}
              gates={gates}
              index={index}
              key={dragCard.gateType}
              title={dragCard.gateType}
              type={GATE_TYPES.NOT_IN_PROGRESS}
              zIndex={gateZIndex--}
              onGatesUpdated={handleUpdatedGates}
              onInvalidDrag={addDragTitles}
              onRemoveInvalidDrags={removeDragTitles}
            />
          ))}
        </div>
      </div>
      <div>
        <div className="d-flex align-items-cener justify-content-between">
          <div className="fw-semibold mb-1">Final Progress</div>
          <UnusedAccessLevelsTooltip dragIdentifier={dragIdentifier} unusedAccessLevels={unusedAccessLevels}/>
        </div>
        <div className="d-flex panel panel-default soft-panel m-0 gate-container harsher-border">
          {gates[GATE_TYPES.IN_PROGRESS].map((dragCard, index) => (
            <MoveableGate
              allowedDropLocations={allowDropForGate(dragCard.gateType, index, GATE_TYPES.IN_PROGRESS)}
              dragIdentifier={dragIdentifier}
              gates={gates}
              index={index}
              key={dragCard.gateType}
              previousGateType={gates[GATE_TYPES.IN_PROGRESS][index - 1]?.gateType}
              title={dragCard.gateType}
              type={GATE_TYPES.IN_PROGRESS}
              zIndex={gateZIndex--}
              onGatesUpdated={handleUpdatedGates}
              onInvalidDrag={addDragTitles}
              onRemoveInvalidDrags={removeDragTitles}
            />
          ))}
        </div>
      </div>
      <Banner
        content={alertContent}
        noIcon
        show={invalidDragTitles.length > 0}
        variant="danger"
        onClose={() => removeDragTitles(Object.values(GATE_DRAG_OPTIONS))}
      />
    </React.Fragment>
  );
}

export const GATE_PROPS = PropTypes.arrayOf(PropTypes.shape({
  id: PropTypes.number.isRequired,
  order: PropTypes.number.isRequired,
  gateType: PropTypes.string.isRequired,
  requiredBefore: PropTypes.string.isRequired
}));

RoleAccessSettings.propTypes = {
  gates: PropTypes.shape({
    broker: GATE_PROPS,
    owner: GATE_PROPS
  }),
  role: PropTypes.shape({
    label: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired
  }),
  vaultId: PropTypes.number.isRequired,
  onGatesChange: PropTypes.func.isRequired
};
